import { Alert, Box, Group, Select, Stack, Text, Title, Switch } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { DateRangeItemGranularity, DateRangeItemGranularityUnit, SectionProps } from '../../types';
import { getFieldType } from '../../utils';
import { COMPARE_CAPABLE_WIDGET_TYPES } from '../../../types';
import classes from './DateRange.module.css';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';

type RelativeRangeValues = | '15m' | '1h' | '1d' | '1w' | '2w' | '1mo' | '3mo' | '6mo' | '1y' | 'custom';
type DateRangeConfig = {
  field: string | null;
  relativeRange: RelativeRangeValues;
  from: Date | null;
  to: Date | null;
  granularity: DateRangeItemGranularity;
  compare: boolean;
};

export default function DateRangeSection({ getAvailableFields, updateConfig, config, validationErrors }: SectionProps ) {
  const { t } = useTranslation( 'widgets' );

  // validation errors
  const sectionErrors = validationErrors?.dateRange;

  // fields
  const allFieldOptions = useMemo(() => getAvailableFields?.() ?? [], [
    getAvailableFields
  ]);
  const datetimeFieldOptions = useMemo(
    () => allFieldOptions.filter(( f ) => f.type === 'datetime' ),
    [allFieldOptions]
  );
  const datetimeDimension = useMemo(() => {
    const dimensions = config.dimensions || [];
    const datetimeDim = dimensions.find( dim => {
      const fieldType = getFieldType({ getAvailableFields, field: dim.field });
      return fieldType === 'datetime';
    });
    return datetimeDim;
  }, [ config.dimensions, getAvailableFields ]);

  // all-time toggle
  const [ isAllTime, setIsAllTime ] = useState<boolean>( config.dateRange === null );

  const stored = ( config.dateRange ?? {}) as Partial<DateRangeConfig>;
  const [ field, setField ] = useState<string | null>(
    datetimeDimension
      ? datetimeDimension.field
      : stored.field ?? null
  );
  const [ relativeRange, setRelativeRange ] = useState<RelativeRangeValues>(
    stored.relativeRange ?? '1mo'
  );
  const [ from, setFrom ] = useState<Date | null>( stored.from ?? new Date());
  const [ to, setTo ] = useState<Date | null>( stored.to ?? new Date());
  const [ gran, setGran ] = useState<DateRangeItemGranularity>(
    stored.granularity ?? { value: 1, unit: 'day' }
  );

  // TV1-1930 — period-over-period compare. Offered ONLY for widget types that can
  // consume the `{ current, prior }` payload (`COMPARE_CAPABLE_WIDGET_TYPES` =
  // `kpi`). A compare result cannot go through `rawData()` at all — it throws —
  // so handing it to a chart parser would hand Mantine a non-array `data` prop.
  const supportsCompare = COMPARE_CAPABLE_WIDGET_TYPES.has( config.type );
  const [ compare, setCompare ] = useState<boolean>( stored.compare ?? false );

  const relativeRangeOptionsTranslated = useMemo(
    () => [
      { value: '15m', label: t( 'widget_builder_date_range_option_15m', 'Last 15 minutes' ) },
      { value: '1h', label: t( 'widget_builder_date_range_option_1h', 'Last hour' ) },
      { value: '1d', label: t( 'widget_builder_date_range_option_1d', 'Last day' ) },
      { value: '1w', label: t( 'widget_builder_date_range_option_1w', 'Last week' ) },
      { value: '2w', label: t( 'widget_builder_date_range_option_2w', 'Last 2 weeks' ) },
      { value: '1mo', label: t( 'widget_builder_date_range_option_1mo', 'Last month' ) },
      { value: '3mo', label: t( 'widget_builder_date_range_option_3mo', 'Last 3 months' ) },
      { value: '6mo', label: t( 'widget_builder_date_range_option_6mo', 'Last 6 months' ) },
      { value: '1y', label: t( 'widget_builder_date_range_option_1y', 'Last year' ) },
      { value: 'custom', label: t( 'widget_builder_date_range_option_custom', 'Custom range' ) }
    ],
    [t]
  );

  const timeUnitOptionsTranslated = useMemo(
    () => [
      { value: 'minute', label: t( 'time_unit_minutes', 'minutes' ) },
      { value: 'hour', label: t( 'time_unit_hours', 'hours' ) },
      { value: 'day', label: t( 'time_unit_days', 'days' ) },
      { value: 'week', label: t( 'time_unit_weeks', 'weeks' ) },
      { value: 'month', label: t( 'time_unit_months', 'months' ) },
      { value: 'year', label: t( 'time_unit_years', 'years' ) }
    ],
    [t]
  );

  // default config helper
  const setDefaultConfig = () => {
    const defaultField =
      datetimeDimension?.field ?? datetimeFieldOptions[0]?.value ?? null;
    if ( !defaultField ) return;
    setField( defaultField );
    setRelativeRange( '1mo' );
    setGran({ value: 1, unit: 'day' });
    updateConfig( 'dateRange', {
      field: defaultField,
      relativeRange: '1mo',
      start: '',
      end: '',
      granularity: { value: 1, unit: 'day' }
    });
  };

  // sync with dimension add/remove
  useEffect(() => {
    if ( datetimeDimension ) {
      setField( datetimeDimension.field );
      setIsAllTime( false );
      setDefaultConfig();
    }
  }, [datetimeDimension]);

  const handleAllTimeToggle = ( checked: boolean ) => {
    setIsAllTime( checked );
      if ( checked ) {
      // Set dateRange to null when 'All Time' is enabled
      updateConfig( 'dateRange', null );
    } else {
      // Set default values when 'All Time' is disabled
      setDefaultConfig();
    }
  };

  // sync when any changes
   useEffect(() => {
    if ( field && !isAllTime ) {
      const dateRangeConfig: any = {
        field,
        relativeRange,
        granularity: {
          value: gran.value,
          unit: gran.unit
        }
      };

      // Only include start/end for custom ranges
      if ( relativeRange === 'custom' ) {
        dateRangeConfig.start = from ? from.toISOString() : '';
        dateRangeConfig.end = to ? to.toISOString() : '';
      }

      // TV1-1930 — only persist `compare` for a type that can actually consume the
      // comparison. Switching a KPI to a bar chart must not leave a stale
      // `compare: true` behind on the definition.
      if ( supportsCompare && compare ) {
        dateRangeConfig.compare = true;
      }

      updateConfig( 'dateRange', dateRangeConfig );
    }
  },[ field, relativeRange, from, to, gran, updateConfig, isAllTime, compare, supportsCompare ]);

  // reset custom whenever range changes
  useEffect(() => {
    if ( relativeRange !== 'custom' ) {
      const newFrom = new Date();
      const now = new Date();
      setFrom( newFrom );
      setTo( now );
    }
  }, [relativeRange]);

  // change handlers
  const handleField = ( v: string | null ) => setField( v );
   const handleRelativeRange = ( v: string | null ) => setRelativeRange( v as RelativeRangeValues );

  const handleFrom = ( d: Date | null ) => {
    setFrom( d );
  };
  const handleTo = ( d: Date | null ) => {
    setTo( d );
  };
  const handleGranAmount = ( value: string ) => {
    const numericValue = value.replace( /[^0-9]/g, '' );
    const finalValue = numericValue === '' ? '1' : numericValue;
    setGran({ value: parseInt( finalValue, 10 ), unit: gran.unit });
  };

  const handleGranUnit = ( value: string | null ) => {
    if ( value ) {
      setGran({ value: gran.value, unit: value as DateRangeItemGranularityUnit });
    }
  };
  const isGranularityRequired = Boolean( datetimeDimension );

  return (
    <div className={classes.container}>
      <Title order={4} className={classes.sectionTitle}>
        {t( 'widget_builder_date_range_title', 'Date Range' )}
      </Title>

      <Alert icon={<IconInfoCircle size="1.2rem" />} color="blue.5" variant="light" mb="xs">
        <Text size="sm">
          {t(
            'widget_builder_date_range_description',
            'Configure date-based filtering to show data within specific time periods.'
          )}
          {datetimeDimension && ' '}
          {datetimeDimension &&
            t(
              'widget_builder_date_range_description_extra',
              'A datetime dimension is selected, so granularity is required.'
            )}
        </Text>
      </Alert>

      {sectionErrors && Object.keys( sectionErrors ).length > 0 && (
        <Alert icon={<IconAlertCircle size="1.2rem" />} color="red" variant="light" mb="xs">
          <Text size="sm" c="red.9">
            {t(
              'widget_builder_date_range_errors_message',
              'Please fix the following errors:'
            )}
           <ul style={{ marginTop: '8px', marginBottom: '0' }}>
              {Object.entries( sectionErrors ).map(([ f, err ]) => (
                <li key={f}>{err}</li>
              ))}
            </ul>
          </Text>
        </Alert>
      )}

      <Box className={classes.content}>
        {!datetimeDimension && (
          <Switch
            label={t( 'widget_builder_date_range_all_time_label', 'All Time' )}
            checked={isAllTime}
            onChange={( e ) => handleAllTimeToggle( e.currentTarget.checked )}
            mb="md"
          />
        )}

        {!isAllTime && (
          <Stack gap="sm">
            <Select
              label={t( 'widget_builder_date_range_field_label', 'Field' )}
              placeholder={t(
                'widget_builder_date_range_field_placeholder',
                'Choose a datetime field'
              )}
              data={datetimeFieldOptions}
              value={field}
              onChange={handleField}
              disabled={Boolean( datetimeDimension )}
              clearable={!datetimeDimension}
              searchable
              radius="xs"
              checkIconPosition="right"
            />

            {datetimeDimension && (
              <Text size="sm" c="dimmed">
                {t( 'widget_builder_date_range_using_dimension', 'Using dimension field:' )}{' '}
                {datetimeDimension.field}
              </Text>
            )}

            <Select
              label={t(
                'widget_builder_date_range_relative_range_label',
                'Relative Range'
              )}
              placeholder={t(
                'widget_builder_date_range_relative_range_placeholder',
                'Select range'
              )}
              data={relativeRangeOptionsTranslated}
              value={relativeRange}
              onChange={handleRelativeRange}
              clearable={false}
              radius="xs"
              checkIconPosition="right"
            />

            {relativeRange === 'custom' && (
              <Group grow align="flex-end">
                <DateTimePicker
                  label={t( 'widget_builder_date_range_from_label', 'From' )}
                  value={from}
                  maxDate={to ?? undefined}
                  onChange={handleFrom}
                  clearable={false}
                  radius='xs'
                />
                <DateTimePicker
                  label={t( 'widget_builder_date_range_to_label', 'To' )}
                  value={to}
                  minDate={from ?? undefined}
                  onChange={handleTo}
                  clearable={false}
                  radius='xs'
                />
              </Group>
            )}

            {supportsCompare && (
              <Switch
                label={t( 'widget_builder_date_range_compare_label', 'Compare with previous period' )}
                description={t(
                  'widget_builder_date_range_compare_description',
                  'Also fetch the immediately preceding window of the same length, so the widget can show a period-over-period delta.'
                )}
                checked={compare}
                onChange={( event ) => setCompare( event.currentTarget.checked )}
                data-testid={componentsTestIds.dateRangeCompareToggle}
              />
            )}

            <Group grow>
              <Box>
                <Text size="sm" fw={500} mb={3}>
                  {t( 'widget_builder_date_range_granularity_label', 'Granularity' )}{' '}
                  {isGranularityRequired && <span style={{ color: 'red' }}>*</span>}
                </Text>
                <Group grow>
                  <input
                    type="number"
                    min="1"
                    max="1"
                    value={gran.value}
                    onChange={( e ) => handleGranAmount( e.target.value )}
                    disabled
                     style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #ced4da',
                      fontSize: '14px',
                      width: '100%',
                      height: '36px'
                    }}
                  />
                  <Select
                    data={timeUnitOptionsTranslated}
                    value={gran.unit}
                    onChange={handleGranUnit}
                    radius="xs"
                    checkIconPosition="right"
                    style={{ flexGrow: 2 }}
                  />
                </Group>
                {isGranularityRequired && (
                  <Text size="xs" c="dimmed" mt={5}>
                    {t(
                      'widget_builder_date_range_granularity_help',
                      'Granularity is required when using a datetime dimension'
                    )}
                  </Text>
                )}
              </Box>
            </Group>
          </Stack>
        )}
      </Box>
    </div>
  );
}
